import { useState } from "react";
import { X, Check, ArrowLeft, ArrowRight, Link2, Play } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { mediaList, parseEmbed } from "@/lib/media";
import { Input } from "@/components/ui/input";
import MediaUpload from "@/components/MediaUpload";

// Edits {image, images, media_types, posters} on a service/portfolio item.
export const MediaEditor = ({ value, onChange }) => {
  const [link, setLink] = useState("");
  const list = mediaList({ ...value, images: value.images || [] });
  const commit = (items, cover = value.image) => {
    const urls = items.map((m) => m.url);
    onChange({
      images: urls, media_types: items.map((m) => m.kind), posters: items.map((m) => m.poster || ""),
      image: urls.includes(cover) ? cover : urls[0] || "",
    });
  };
  const add = (m) => commit([...list, m], value.image || m.url);
  const addLink = () => {
    if (!parseEmbed(link.trim())) return toast.error("Paste a valid YouTube or Vimeo link");
    add({ url: link.trim(), kind: "embed", poster: "" });
    setLink("");
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    commit(arr);
  };

  return (
    <div data-testid="media-editor">
      <span className="text-white/60 text-xs uppercase tracking-wide font-mono-label mb-2 block">Media ({list.length}) — gold border = cover shown on cards</span>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {list.map((m, i) => {
          const thumb = m.kind === "embed" ? parseEmbed(m.url).thumb : m.kind === "video" ? (m.poster && mediaUrl(m.poster)) : mediaUrl(m.url);
          const isCover = value.image === m.url;
          return (
            <div key={`${m.url}-${i}`} className={`relative rounded-lg overflow-hidden border-2 ${isCover ? "border-[#D4AF37]" : "border-white/10"}`} data-testid={`media-item-${i}`}>
              {thumb ? <img src={thumb} alt="" className="h-20 w-full object-cover" /> : <div className="h-20 bg-[#0a0a0a]" />}
              {m.kind !== "image" && <span className="absolute top-1 left-1 bg-black/70 text-[#D4AF37] text-[8px] px-1 rounded flex items-center gap-0.5"><Play size={8} />{m.kind === "embed" ? "LINK" : "VIDEO"}</span>}
              <button type="button" onClick={() => commit(list.filter((_, k) => k !== i))} data-testid={`media-remove-${i}`} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white"><X size={12} /></button>
              <div className="absolute bottom-1 inset-x-1 flex items-center justify-between">
                {isCover ? <span className="bg-[#D4AF37] text-black text-[8px] px-1 rounded">COVER</span>
                  : <button type="button" onClick={() => commit(list, m.url)} data-testid={`make-cover-${i}`} className="bg-black/70 text-white text-[8px] px-1 rounded flex items-center gap-0.5"><Check size={8} /> Cover</button>}
                <span className="flex gap-0.5">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`media-left-${i}`} className="bg-black/70 rounded p-0.5 text-white disabled:opacity-30"><ArrowLeft size={10} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} data-testid={`media-right-${i}`} className="bg-black/70 rounded p-0.5 text-white disabled:opacity-30"><ArrowRight size={10} /></button>
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <MediaUpload label="Upload image or video (MP4/MOV)" value="" onChange={(url, isVideo, poster) => url && add({ url, kind: isVideo ? "video" : "image", poster: poster || "" })} />
      <div className="flex gap-2 mt-3">
        <div className="relative flex-1">
          <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())}
            placeholder="Paste YouTube or Vimeo link" className="pl-8 bg-[#0a0a0a] border-white/15" data-testid="media-link-input" />
        </div>
        <button type="button" onClick={addLink} data-testid="media-link-add" className="rounded-lg px-4 text-sm border border-white/20 text-white/80 hover:border-[#D4AF37]">Add link</button>
      </div>
    </div>
  );
};
